import { fmtTL } from "@/components/StatusBadge";
import { QRCodeSVG } from "qrcode.react";
import { COMPANY, verifyUrl, logoUrl, docImageUrl, docStampUrl, docStampPos, activeDocTheme, activeDocTemplate, docBrandName, docShowBrandText, docLegalName, docAddress, docTaxLine, docPhone, docWeb, docBankAccounts, docCfg } from "@/lib/company";

const ACC_LBL = { cash: "Nakit", bank: "Banka / Havale-EFT", credit: "Kredi Kartı", cek: "Çek", senet: "Senet" };

const dt = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const dOnly = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return v; }
};

// Tutarı Türkçe okunuşa çevirir (makbuz "yalnız ... TL" satırı için).
const ONES = ["", "bir", "iki", "üç", "dört", "beş", "altı", "yedi", "sekiz", "dokuz"];
const TENS = ["", "on", "yirmi", "otuz", "kırk", "elli", "altmış", "yetmiş", "seksen", "doksan"];
function group3(n) {
  let s = "";
  const y = Math.floor(n / 100), k = Math.floor((n % 100) / 10), b = n % 10;
  if (y) s += (y > 1 ? ONES[y] + " " : "") + "yüz ";
  if (k) s += TENS[k] + " ";
  if (b) s += ONES[b] + " ";
  return s.trim();
}
function numToTr(n) {
  n = Math.floor(Math.abs(n));
  if (n === 0) return "sıfır";
  const parts = [];
  const milyon = Math.floor(n / 1000000); n %= 1000000;
  const bin = Math.floor(n / 1000); n %= 1000;
  if (milyon) parts.push(group3(milyon) + " milyon");
  if (bin) parts.push((bin === 1 ? "" : group3(bin) + " ") + "bin");
  if (n) parts.push(group3(n));
  return parts.join(" ").replace(/\s+/g, " ").trim();
}
function amountInWords(total) {
  const lira = Math.floor(Math.abs(total));
  const kurus = Math.round((Math.abs(total) - lira) * 100);
  let s = numToTr(lira) + " TL";
  if (kurus) s += " " + numToTr(kurus) + " Kr";
  return s.charAt(0).toLocaleUpperCase("tr-TR") + s.slice(1);
}

export const FINANCE_DOC_CSS = `
  #finance-doc-root { color:#0A0A0A; font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif; font-size:12px; background:#fff; --fd-primary:#064E3B; --fd-accent:#EA580C; --fd-soft:#ECFDF5; --fd-border:#d1fae5; }
  #finance-doc-root * { box-sizing:border-box; }
  #finance-doc-root .fd-band { height:9px; background:var(--fd-primary); border-radius:5px 5px 0 0; overflow:hidden; }
  #finance-doc-root .fd-band i { display:block; height:100%; width:38%; background:var(--fd-accent); }
  #finance-doc-root .fd-head { display:flex; justify-content:space-between; align-items:flex-start; gap:16px; padding:14px 4px 12px; }
  #finance-doc-root .fd-logo { height:50px; width:auto; display:block; margin-bottom:6px; }
  #finance-doc-root .fd-brand { font-size:16px; font-weight:800; color:var(--fd-primary); letter-spacing:.3px; }
  #finance-doc-root .fd-legal { font-weight:700; font-size:11px; }
  #finance-doc-root .fd-sm { font-size:10.5px; color:#4B5563; line-height:1.55; }
  #finance-doc-root .fd-titlewrap { text-align:right; min-width:200px; }
  #finance-doc-root .fd-title { font-size:22px; font-weight:800; letter-spacing:1px; color:var(--fd-primary); line-height:1.05; }
  #finance-doc-root .fd-kind { display:inline-block; margin-top:4px; font-size:11px; font-weight:700; padding:2px 10px; border-radius:999px; }
  #finance-doc-root .fd-kind.inc { background:#dcfce7; color:#15803d; }
  #finance-doc-root .fd-kind.exp { background:#fee2e2; color:#b91c1c; }
  #finance-doc-root .fd-metabox { margin-top:8px; background:var(--fd-soft); border:1px solid var(--fd-border); border-radius:8px; padding:8px 10px; font-size:10.5px; text-align:left; }
  #finance-doc-root .fd-metabox .m { display:flex; justify-content:space-between; gap:18px; padding:2px 0; }
  #finance-doc-root .fd-metabox .m b { color:var(--fd-primary); }
  #finance-doc-root .fd-parties { display:flex; gap:12px; margin:6px 0 12px; align-items:stretch; }
  #finance-doc-root .fd-party { flex:1; border:1px solid #e2e8f0; border-radius:8px; padding:9px 11px; }
  #finance-doc-root .fd-party.hi { border-left:4px solid var(--fd-accent); background:#fff7ed; }
  #finance-doc-root .fd-party .lbl { font-size:9px; text-transform:uppercase; letter-spacing:1px; color:var(--fd-accent); font-weight:700; margin-bottom:3px; }
  #finance-doc-root .fd-party .nm { font-weight:800; font-size:13px; }
  #finance-doc-root .fd-qr { text-align:center; flex:0 0 auto; }
  #finance-doc-root .fd-cap { font-size:9px; color:#4B5563; margin-top:3px; letter-spacing:.3px; }
  #finance-doc-root .fd-table { width:100%; border-collapse:collapse; margin-top:2px; border-radius:8px; overflow:hidden; }
  #finance-doc-root .fd-table th { background:var(--fd-primary); color:#fff; font-size:10.5px; text-align:left; padding:8px; font-weight:600; }
  #finance-doc-root .fd-table td { border-bottom:1px solid #eef2f7; padding:8px; font-size:11.5px; }
  #finance-doc-root .fd-table .r, #finance-doc-root .fd-table th.r { text-align:right; }
  #finance-doc-root .fd-words { margin-top:12px; background:var(--fd-soft); border:1px dashed var(--fd-border); border-radius:8px; padding:9px 11px; font-size:12px; }
  #finance-doc-root .fd-words b { color:var(--fd-primary); }
  #finance-doc-root .fd-bottom { display:flex; gap:14px; margin-top:12px; align-items:flex-start; }
  #finance-doc-root .fd-note { flex:1; border:1px solid var(--fd-border); border-radius:8px; padding:9px 11px; font-size:10.5px; color:#334155; background:var(--fd-soft); line-height:1.6; }
  #finance-doc-root .fd-note b { color:var(--fd-primary); }
  #finance-doc-root .fd-totals { width:46%; min-width:210px; }
  #finance-doc-root .fd-trow { display:flex; justify-content:space-between; padding:4px 2px; font-size:11.5px; color:#334155; }
  #finance-doc-root .fd-grand { margin-top:6px; background:var(--fd-primary); border-radius:8px; padding:10px 12px; display:flex; justify-content:space-between; align-items:center; }
  #finance-doc-root .fd-grand .gl { color:#fff; font-weight:700; font-size:12px; letter-spacing:.5px; }
  #finance-doc-root .fd-grand .gv { color:#fff; font-weight:800; font-size:19px; }
  #finance-doc-root .fd-foot { display:flex; justify-content:space-between; align-items:flex-end; margin-top:24px; border-top:2px solid var(--fd-primary); padding-top:10px; gap:12px; }
  #finance-doc-root .fd-sline { border-top:1px dotted #94a3b8; width:170px; height:40px; margin:0 auto 4px; }
  /* ---- Belge Şablonları (klasik = varsayılan, kural yok) ---- */
  #finance-doc-root[data-template="modern"] .fd-band { height:16px; }
  #finance-doc-root[data-template="modern"] .fd-band i { width:55%; }
  #finance-doc-root[data-template="modern"] .fd-title { font-size:26px; text-transform:uppercase; }
  #finance-doc-root[data-template="modern"] .fd-table th { text-transform:uppercase; letter-spacing:.8px; }
  #finance-doc-root[data-template="modern"] .fd-head { padding-top:18px; }
  #finance-doc-root[data-template="minimal"] .fd-band { display:none; }
  #finance-doc-root[data-template="minimal"] .fd-title { color:#0A0A0A; }
  #finance-doc-root[data-template="minimal"] .fd-table th { background:#fff; color:#0A0A0A; border-bottom:2px solid #0A0A0A; }
  #finance-doc-root[data-template="minimal"] .fd-metabox { background:#fff; }
  #finance-doc-root[data-template="minimal"] .fd-grand { background:#0A0A0A; }
  #finance-doc-root[data-template="minimal"] .fd-note { background:#fff; }
  #finance-doc-root[data-template="minimal"] .fd-words { background:#fff; }
  #finance-doc-root[data-template="zarif"] { font-family:'Georgia','Times New Roman',serif; }
  #finance-doc-root[data-template="zarif"] .fd-band { height:5px; border-radius:0; }
  #finance-doc-root[data-template="zarif"] .fd-title { font-weight:700; letter-spacing:3px; }
  #finance-doc-root[data-template="zarif"] .fd-table th { background:var(--fd-soft); color:var(--fd-primary); border-bottom:2px solid var(--fd-primary); }
  #finance-doc-root[data-template="kompakt"] { font-size:10.5px; }
  #finance-doc-root[data-template="kompakt"] .fd-head { padding:8px 4px 7px; }
  #finance-doc-root[data-template="kompakt"] .fd-title { font-size:18px; }
  #finance-doc-root[data-template="kompakt"] .fd-table th { padding:5px; font-size:9.5px; }
  #finance-doc-root[data-template="kompakt"] .fd-table td { padding:5px; font-size:10px; }
  #finance-doc-root[data-template="kompakt"] .fd-parties { margin:4px 0 8px; }
  #finance-doc-root[data-template="kurumsal"] { border:2px solid var(--fd-primary); }
  #finance-doc-root[data-template="kurumsal"] .fd-band { display:none; }
  #finance-doc-root[data-template="kurumsal"] .fd-head { border-bottom:3px double var(--fd-primary); padding-top:14px; }
  #finance-doc-root[data-template="kurumsal"] .fd-title { border:2px solid var(--fd-primary); padding:2px 10px; border-radius:6px; }
  #finance-doc-root[data-template="premium"] .fd-band { height:20px; }
  #finance-doc-root[data-template="premium"] .fd-band i { width:100%; background:var(--fd-accent); }
  #finance-doc-root[data-template="premium"] .fd-table th { border-bottom:2px solid var(--fd-accent); }
  #finance-doc-root[data-template="premium"] .fd-grand { border:1px solid var(--fd-accent); }
  #finance-doc-root[data-template="ferah"] .fd-band { height:4px; }
  #finance-doc-root[data-template="ferah"] .fd-head { padding-top:20px; padding-bottom:14px; }
  #finance-doc-root[data-template="ferah"] .fd-table td { padding:10px 8px; }
  #finance-doc-root[data-template="ferah"] .fd-table th { background:var(--fd-soft); color:var(--fd-primary); }
  #finance-doc-root[data-template="kutu"] .fd-band { display:none; }
  #finance-doc-root[data-template="kutu"] .fd-head { border:1.5px solid var(--fd-primary); border-radius:10px; padding:12px; }
  #finance-doc-root[data-template="kutu"] .fd-table { border:1.5px solid var(--fd-primary); border-radius:10px; overflow:hidden; }
  #finance-doc-root[data-template="kutu"] .fd-grand { border:1.5px solid var(--fd-primary); }
`;

// Belgeyi izole yeni pencerede A5 yazdırır (termal 80mm CSS ile çakışmaz).
export function printFinanceNode() {
  const node = document.getElementById("finance-doc-root");
  if (!node) return false;
  const w = window.open("", "_blank", "width=800,height=1050");
  if (!w) return false;
  w.document.write(`<html><head><title>Belge</title><style>@page{size:A5;margin:8mm}body{margin:0;padding:0;color:#000;-webkit-print-color-adjust:exact;print-color-adjust:exact}${FINANCE_DOC_CSS}</style></head><body>${node.outerHTML}<script>window.onload=function(){setTimeout(function(){window.print();setTimeout(function(){window.close()},400)},300)}<\/script></body></html>`);
  w.document.close();
  return true;
}

// type: "makbuz" (tahsilat/ödeme makbuzu) | "dekont" (banka/ödeme dekontu)
export function FinanceDocA5({ entry, type = "makbuz", docConfig }) {
  if (!entry) return null;
  const isIncome = entry.kind === "income";
  const th = activeDocTheme();
  const themeVars = { "--fd-primary": th.primary, "--fd-accent": th.accent, "--fd-soft": th.primarySoft, "--fd-border": th.primaryBorder };
  const ref = entry.doc_no || entry.id || "";
  const cari = entry.cari_name || "Nihai Tüketici / İlgili";
  const cfg = docConfig || docCfg("finance");
  const showBrand = cfg.show_brand && docShowBrandText();
  const title = cfg.title || (type === "dekont" ? "DEKONT" : (isIncome ? "TAHSİLAT MAKBUZU" : "ÖDEME MAKBUZU"));
  const total = Number(entry.gross || entry.amount || 0);

  return (
    <div id="finance-doc-root" data-testid="finance-doc-root" data-template={activeDocTemplate()} style={themeVars}>
      <div className="fd-band"><i /></div>
      <div className="fd-head">
        <div>
          {cfg.show_logo && <img className="fd-logo" src={logoUrl()} alt={docBrandName()} />}
          {showBrand && <div className="fd-brand">{docBrandName()}</div>}
          <div className="fd-legal">{docLegalName()}</div>
          <div className="fd-sm">{docAddress()}</div>
          <div className="fd-sm">{docTaxLine()}</div>
          <div className="fd-sm">{docPhone()} · {docWeb()}</div>
          {cfg.header_lines.map((l, i) => <div key={i} className="fd-sm" data-testid="finance-header-line">{l}</div>)}
        </div>
        {cfg.stamp && docStampPos() === "top" && (
          <div style={{ flex: "0 0 auto", alignSelf: "center", textAlign: "center", padding: "0 8px" }} data-testid="finance-stamp-top">
            {docStampUrl()
              ? <img src={docImageUrl(docStampUrl())} alt="Kaşe & İmza" data-testid="finance-stamp-img" style={{ display: "inline-block", maxHeight: "72px", maxWidth: "150px" }} />
              : <span data-testid="finance-stamp-text" style={{ display: "inline-block", transform: "rotate(-5deg)", border: "2px solid #064E3B", color: "#064E3B", borderRadius: "8px", padding: "6px 16px", fontSize: "11px", fontWeight: 800, letterSpacing: "2px", lineHeight: 1.35, textAlign: "center" }}>RESMÎ BELGE<br /><span style={{ fontSize: "9px", fontWeight: 700 }}>{docBrandName()}</span></span>}
          </div>
        )}
        <div className="fd-titlewrap">
          <div className="fd-title">{title}</div>
          <div><span className={`fd-kind ${isIncome ? "inc" : "exp"}`}>{isIncome ? "GELİR / TAHSİLAT" : "GİDER / ÖDEME"}</span></div>
          <div className="fd-metabox">
            <div className="m"><b>Belge No</b><span>{ref || "—"}</span></div>
            <div className="m"><b>Tarih</b><span>{dOnly(entry.date) || dt(entry.created_at)}</span></div>
            <div className="m"><b>Hesap</b><span>{ACC_LBL[entry.account] || entry.account || "—"}</span></div>
          </div>
        </div>
      </div>

      <div className="fd-parties">
        <div className="fd-party hi">
          <div className="lbl">{isIncome ? "Ödeyen (Sayın)" : "Tahsil Eden (Sayın)"}</div>
          <div className="nm">{cari}</div>
          {entry.location_name && <div className="fd-sm">{entry.location_name}</div>}
        </div>
        {cfg.qr_verify && (
          <div className="fd-qr">
            <QRCodeSVG value={verifyUrl(ref)} size={68} level="M" />
            <div className="fd-cap">Belge Doğrula</div>
          </div>
        )}
      </div>

      {type === "dekont" ? (
        <table className="fd-table">
          <thead><tr><th>Açıklama</th><th>Kategori</th><th className="r">Net</th><th className="r">KDV %{Math.round(entry.vat_rate || 0)}</th><th className="r">Tutar</th></tr></thead>
          <tbody>
            <tr>
              <td>{entry.description || (isIncome ? "Tahsilat" : "Ödeme")}</td>
              <td>{entry.category || "—"}</td>
              <td className="r">{fmtTL(entry.amount)}</td>
              <td className="r">{fmtTL(entry.vat_amount)}</td>
              <td className="r">{fmtTL(total)}</td>
            </tr>
          </tbody>
        </table>
      ) : (
        <table className="fd-table">
          <thead><tr><th style={{ width: "26px" }}>#</th><th>Açıklama / Konu</th><th className="r">Tutar</th></tr></thead>
          <tbody>
            <tr>
              <td>1</td>
              <td>{entry.description || entry.category || (isIncome ? "Tahsilat bedeli" : "Ödeme bedeli")}{entry.category && entry.description ? ` · ${entry.category}` : ""}</td>
              <td className="r">{fmtTL(entry.amount)}</td>
            </tr>
            {Number(entry.vat_amount) > 0 && (
              <tr><td>2</td><td>KDV (%{Math.round(entry.vat_rate || 0)})</td><td className="r">{fmtTL(entry.vat_amount)}</td></tr>
            )}
          </tbody>
        </table>
      )}

      <div className="fd-words" data-testid="finance-doc-words">
        <b>Yalnız:</b> {amountInWords(total)}
      </div>

      <div className="fd-bottom">
        <div className="fd-note">
          <b>Açıklama</b><br />
          {entry.description || "—"}
          <br /><b>Ödeme Şekli:</b> {ACC_LBL[entry.account] || entry.account || "—"}
          {type === "dekont" && (() => { const b = docBankAccounts()[0] || {}; const s = [b.bank_name, b.iban].filter(Boolean).join(" · "); return s ? <><br /><b>Banka / IBAN:</b> {s}</> : null; })()}
        </div>
        <div className="fd-totals">
          <div className="fd-trow"><span>Ara Toplam (KDV hariç)</span><span>{fmtTL(entry.amount)}</span></div>
          <div className="fd-trow"><span>KDV (%{Math.round(entry.vat_rate || 0)})</span><span>{fmtTL(entry.vat_amount)}</span></div>
          <div className="fd-grand"><span className="gl">{isIncome ? "TAHSİL EDİLEN" : "ÖDENEN"}</span><span className="gv" data-testid="finance-doc-total">{fmtTL(total)}</span></div>
        </div>
      </div>

      {cfg.footer_lines.length > 0 && (
        <div className="fd-note" style={{ marginTop: "10px" }} data-testid="finance-footer">
          {cfg.footer_lines.map((l, i) => <div key={i} data-testid="finance-footer-line">{l}</div>)}
        </div>
      )}
      <div className="fd-foot">
        <div style={{ textAlign: docStampPos() === "top" ? "center" : docStampPos() }}>
          <div className="fd-sline" />
          <div className="fd-sm">Teslim Eden / Kaşe-İmza</div>
          {cfg.stamp && docStampPos() !== "top" && (docStampUrl()
            ? <img src={docImageUrl(docStampUrl())} alt="Kaşe & İmza" data-testid="finance-stamp-img" style={{ marginTop: "8px", display: "inline-block", maxHeight: "72px", maxWidth: "150px" }} />
            : <span data-testid="finance-stamp-text" style={{ marginTop: "10px", display: "inline-block", transform: "rotate(-5deg)", border: "2px solid #064E3B", color: "#064E3B", borderRadius: "8px", padding: "6px 16px", fontSize: "11px", fontWeight: 800, letterSpacing: "2px", lineHeight: 1.35, textAlign: "center" }}>RESMÎ BELGE<br /><span style={{ fontSize: "9px", fontWeight: 700 }}>{docBrandName()}</span></span>)}
        </div>
        <div style={{ textAlign: "center" }}>
          <div className="fd-sline" />
          <div className="fd-sm">Teslim Alan / İmza</div>
        </div>
      </div>
    </div>
  );
}
